// File attachments for any CRM record. Upload, list, download, delete.
// Currently stores locally on the backend; can swap to SharePoint via Graph later.
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, Trash2, Paperclip } from "lucide-react";
import { getToken } from "@/lib/auth-token";

interface Props {
  entityType: "Account" | "Contact" | "Deal" | "Lead" | "JobOrder" | "Candidate" | "GovOpportunity" | "Project" | "Campaign" | "Feedback";
  entityId: string;
}

function formatBytes(n?: number | null): string {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function AttachmentsPanel({ entityType, entityId }: Props) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const queryKey = ["/attachments", entityType, entityId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () =>
      (await api.get("/attachments", { params: { entityType, entityId } })).data.data as any[],
  });

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("entityType", entityType);
      fd.append("entityId", entityId);
      fd.append("file", file);
      return (await api.post("/attachments", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })).data;
    },
    onSuccess: () => { setError(null); qc.invalidateQueries({ queryKey }); },
    onError: (err: any) => setError(err?.response?.data?.message ?? err?.message ?? "Upload failed."),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/attachments/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadMut.mutate(file);
    if (fileRef.current) fileRef.current.value = "";
  }

  /** Open the file stream. Includes the bearer token as a query param via a fetch-then-open dance. */
  async function download(a: any) {
    const token = getToken();
    const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api/v1";
    const res = await fetch(`${base}/attachments/${a.id}/file`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) { setError(`Download failed (${res.status})`); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = a.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          onChange={onPick}
          className="hidden"
        />
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={uploadMut.isPending}
          size="sm"
        >
          <Upload className="mr-1 h-4 w-4" />
          {uploadMut.isPending ? "Uploading…" : "Upload file"}
        </Button>
        <span className="text-xs text-muted-foreground">Max 25 MB per file.</span>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {isLoading && <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && (data?.length ?? 0) === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">No attachments yet.</p>
      )}

      {(data?.length ?? 0) > 0 && (
        <ul className="divide-y rounded-md border bg-card">
          {data!.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 p-3 text-sm">
              <div className="flex min-w-0 items-center gap-3">
                <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <button
                    onClick={() => download(a)}
                    className="truncate text-left font-medium hover:underline"
                  >
                    {a.fileName}
                  </button>
                  <div className="text-xs text-muted-foreground">
                    {formatBytes(a.sizeBytes)} · {a.uploader?.name ?? "—"} · {new Date(a.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {a.mimeType?.startsWith("image/") && <Badge variant="outline">Image</Badge>}
                {a.mimeType?.includes("pdf") && <Badge variant="outline">PDF</Badge>}
                <Button variant="ghost" size="icon" onClick={() => download(a)} title="Download">
                  <Download className="h-4 w-4"/>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { if (confirm(`Delete "${a.fileName}"?`)) delMut.mutate(a.id); }}
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4 text-destructive"/>
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
